import { Injectable, Inject } from '@angular/core';
import { FramesService } from '../modules/layout/frames.service';
import { APP_CONFIG, AppConfig, AppConfigFactory } from '@icc/shared/config';
import { StateService } from '@icc/shared/state.service';
import { Common, core } from '@icc/shared/helpers';
import { AlignmentsService } from '../layout/alignments.service';
import { ExtensionsService } from '../layout/extensions.service';
import { ProfileType, Profile, SideProfileActive, Reinforcement, WindowActiveConfiguration } from '@icc/window-door/types';
import { ActiveSash } from '@icc/window-door/types';
import { WindowConfiguration } from '@icc/window-door/types';
import { IccProfileSideColors } from '@icc/construction-elements/types';
import { ModalService } from '@icc/configurator/shared';
import { BrowserProfilesService } from './profiles.service';
import { WindowProfilesListPageComponent } from './profiles-list-page/profiles-list-page.component';
import { ConfiguratorsDataService, EventBusService } from '@icc/shared';
import { ProfilesPriceService } from '@icc/construction-elements/configurator';
import { MullionsService } from '@icc/construction-elements/configurator';
import { PriceColorsService } from '@icc/construction-elements/configurator';
import { ProfileColorsService } from '@icc/construction-elements/configurator';

@Injectable()
export class ProfilesModalService {
    constructor(
        private eventBusService: EventBusService,
        private framesService: FramesService,
        private profilesService: BrowserProfilesService,
        private profilesPriceService: ProfilesPriceService,
        private stateService: StateService,
        private alignmentsService: AlignmentsService,
        private extensionsService: ExtensionsService,
        protected mullionsService: MullionsService,
        @Inject(APP_CONFIG) private config: AppConfigFactory,
        private modalService: ModalService,
        private configuratorsDataService: ConfiguratorsDataService,
        private priceColorsService: PriceColorsService,
        private profileColorsService: ProfileColorsService
    ) {}

    async openProfilesModal(
        conf: WindowActiveConfiguration,
        type: ProfileType | ProfileType[] | null,
        options: { and?: string[]; not?: string[] } | { and?: string[]; not?: string[] }[],
        selectedProfile: Profile | null,
        color?: IccProfileSideColors,
        wood?: SideProfileActive['wood'],
        reinforcement?: Reinforcement,
        overwritePrice: boolean = true,
        side?: 'left' | 'right' | 'top' | 'bottom',
        extension?: SideProfileActive | null,
        frameLength?: number,
        frameRef?: any,
        framePosition?: number,
        mullion?: any | null
    ): Promise<Profile> {
        const systemId = conf ? Number(conf.System.id) : null;
        const pricesForLength = this.profilesService.profilesPricesForLength;
        const profiles = this.profilesService
            .getFilteredProfiles(conf, type, options, false)
            .map((profile) => {
                if (side && profile.type === 'extension') {
                    profile.side = side;
                }
                if (profile.type !== 'threshold' && color) {
                    if (profile.type === 'coupling' && profile.widthOutType) {
                        profile.selectedColor = core.copy(color);
                        profile.selectedWood = wood ? core.copy(wood) : null;
                    } else {
                        this.profileColorsService.setProfileDefaultColors(
                            conf,
                            profile,
                            selectedProfile,
                            color,
                            wood ?? null
                        );
                    }
                }
                let profileOptions: {
                    and?: string[];
                    not?: string[];
                } = {
                    and: [],
                    not: [],
                };
                if (Common.isArray(options) && Common.isArray(type)) {
                    profileOptions = options[type.indexOf(profile.type)];
                } else if (!Common.isArray(options) && !Common.isArray(type)) {
                    profileOptions = options;
                }
                const priceType = this.profilesService.getProfilePriceType(
                    profile.type,
                    profileOptions
                );
                if (profile.type !== 'threshold') {
                    let extensionLength;
                    if (
                        profile.type === 'extension' &&
                        this.config().IccConfig.Configurators.extensionPricesForLength
                    ) {
                        const extensionSide =
                            (Array.isArray(options) &&
                                options[0]?.and?.every((o) => o === 'side')) ||
                            (!Array.isArray(options) &&
                                Array.isArray(options.and) &&
                                options.and.every((o) => o === 'side'))
                                ? 'vertical'
                                : 'horizontal';
                        extensionLength = this.profilesService.getExtensionLength(extensionSide, extension);
                    }
                    if (overwritePrice) {
                        const frameColorTypes = [
                            'frame',
                            'fixed_mullion',
                            'structured_muntin_frame',
                            'false_mullion',
                            'threshold',
                        ];
                        const colorFrom = frameColorTypes.includes(profile.type) ? 'frame' : 'sash';

                        const colorFactor = this.priceColorsService.getSideProfileColorFactor({
                            profile,
                            profileColors:
                                profile.selectedColor && profile.selectedColor
                                    ? profile.selectedColor
                                    : conf.Colors[colorFrom],
                            wood: profile.selectedWood ?? conf.Wood,
                            system: conf.System,
                            colorGroups: this.configuratorsDataService.data.windowColorGroups || [],
                        });

                        profile.price = systemId
                            ? this.profilesPriceService.getProfilePrice(
                                  profile.id,
                                  priceType,
                                  conf.System,
                                  profile.selectedColor
                                      ? profile.selectedColor
                                      : conf.Colors[colorFrom],
                                  this.profilesService.profilesPrices,
                                  null,
                                  pricesForLength,
                                  extensionLength,
                                  colorFactor
                              )
                            : null;
                    }
                } else {
                    const thresholdLength = this.profilesService.getThresholdLength();
                    profile.price = systemId
                        ? this.profilesPriceService.getThresholdPrice(
                              profile.id,
                              'threshold',
                              conf.System,
                              this.profilesService.profilesPrices,
                              null,
                              pricesForLength,
                              thresholdLength
                          )
                        : null;
                }
                return profile;
            });

        const modalInstance = this.modalService.open({
            component: 'profilesModal',
            pageComponent: WindowProfilesListPageComponent,
            resolve: {
                profiles: () => profiles,
                profilesPrices: () => this.profilesService.profilesPrices,
                system: () => conf.System,
                type: () => type,
                selectedProfile: () => selectedProfile,
                currency: () =>
                    this.config().currency || this.stateService.state.offers[0].doc.currency,
                color: () => color,
                wood: () => wood,
                hideCouplings: () => conf.SashesType !== 'Fix',
                reinforcement: () => reinforcement,
                profileCategories: () => this.profilesService.getProfileCategories(),
                isDefaultLength: () => extension?.isDefaultLength === false ? false : true,
                length: () => frameLength ?? extension?.length,
                frame: () => frameRef ?? null,
                framePosition: () => framePosition ?? null,
                mullionId: () => mullion?.id ?? null,
            },
        });

        return modalInstance.result;
    }

    selectAlignmentAndPut(
        field: ActiveSash,
        conf: WindowActiveConfiguration,
        side: 'top' | 'bottom' | 'left' | 'right'
    ) {
        let alignmentType = 'alignment_frame';
        const frame =
            field.frameId != null ? FramesService.getFrameById(field.frameId, conf) : null;
        if (Common.isNumber(field.parentId)) {
            alignmentType = 'alignment_sash';
        } else if (
            side === 'bottom' &&
            field.nearAlignments.bottom === -1 &&
            field.nearAlignments.left === -1 &&
            field.nearAlignments.right === -1 &&
            field.nearMullions.bottom === -1 &&
            frame &&
            frame.lowThreshold
        ) {
            alignmentType = 'alignment_threshold';
        }
        const options = {
            and: [alignmentType],
            not: [],
        };

        this.openProfilesModal(conf, null, options, null, conf.Colors.frame).then(
            (selectedProfile) => {
                if (selectedProfile) {
                    this.alignmentsService.putAlignmentInField(selectedProfile, field, conf, side);
                    if (this.config().IccConfig.Configurators.dependencies) {
                        this.eventBusService.post({ key: 'processDependencies', value: null });
                    }
                }
            }
        );
    }

    selectExtensionAndPut(
        conf: WindowActiveConfiguration,
        side: 'top' | 'bottom' | 'left' | 'right'
    ) {
        const options = [
            {
                and: [side === 'left' || side === 'right' ? 'side' : side],
                not: [],
            },
            {
                and: [],
                not: [],
            },
        ];

        this.openProfilesModal(
            conf,
            ['extension', 'sandwich'],
            options,
            null,
            conf.Colors.frame,
            undefined,
            undefined,
            true,
            side
        ).then((profile) => {
            if (profile) {
                this.extensionsService.putExtensionOnSide(conf, profile, side);
                if (this.config().IccConfig.Configurators.dependencies) {
                    this.eventBusService.post({ key: 'processDependencies', value: null });
                }

                this.eventBusService.post({
                    key: 'putExtensionOnSide',
                    value: {},
                });
            }
        });
    }

    async selectProfileAndPut(
        conf: WindowActiveConfiguration,
        type: ProfileType,
        selected: Profile | null = null,
        selectedColor: IccProfileSideColors | null = null
    ) {
        const options = [
            {
                and: [],
                not: [],
            },
            {
                and: [],
                not: [],
            },
        ];
        const frameColorTypes = [
            'frame',
            'fixed_mullion',
            'structured_muntin_frame',
            'false_mullion',
            'threshold',
            'coupling',
            'extension',
        ];
        const colorFrom = frameColorTypes.includes(type) ? 'frame' : 'sash';
        const selectedProfile = await this.openProfilesModal(
            conf,
            [type],
            options,
            selected,
            selectedColor ?? conf.Colors[colorFrom]
        );
        return selectedProfile;
    }

    // eslint-disable-next-line max-statements
    changeProfile(
        conf: WindowActiveConfiguration,
        defaultConf: WindowActiveConfiguration,
        type: any,
        {
            position = null,
            frame = null,
            sash = null,
            mullion = null,
            extension = null,
            coupling = null,
        }: {
            position?: any;
            frame?: any;
            sash?: any;
            mullion?: any;
            extension?: any;
            coupling?: any;
        }
    ) {
        frame = frame && conf.Frames.find((el) => el.id === frame.id);
        const changeAllProfiles =
            position === 'all' && (type !== 'frame' || (frame && !frame.lowThreshold));
        const changeProfilesWithoutThreshold =
            (position === 'all' || position === 'allFrames') &&
            type === 'frame' &&
            frame &&
            frame.lowThreshold;
        const changeAllFrames = position === 'allFrames' && type === 'frame';
        let color;
        let wood;
        let profileId;
        let options;
        let reinforcement;
        let altFrame = false;
        const drawPosition = position;
        if (type === 'mullion') {
            mullion = this.getMullion(conf, mullion.id);
            profileId = mullion.profileId;
        } else if (type === 'sashFrame') {
            sash = this.getSash(conf, sash.id);
            const sashFrameData = conf.drawData.sashFrame.find((s: any) => s.sashId === sash.id);
            const side =
                (sashFrameData &&
                    sashFrameData.sides[position] &&
                    sashFrameData.sides[position].outerEdge.side) ||
                'bottom';
            position = changeAllProfiles ? 'bottom' : side;
            profileId = sash.frame[position].profileId;
            color = conf.Colors.sash;
        } else if (type === 'frame') {
            const frameData =
                conf.drawData &&
                conf.drawData.frame &&
                (conf.drawData.frame.find((f: any) => f.frameId === frame.id) ??
                    conf.drawData.frame[0]);
            if (typeof position === 'number') {
                const positionMap = this.profilesService.parseFrameDrawDataSides(frameData.sides)
                    .positionMap;
                altFrame = frameData.sides[position].poly.alt;
                position = positionMap[position];
            }
            position = changeProfilesWithoutThreshold ? 1 : changeAllProfiles ? 0 : position;
            profileId = frame.frame[position].profileId;
            if (altFrame && frame.frame[position].alt?.profileId) {
                profileId = frame.frame[position].alt.profileId;
            }
            const currentFrameSide = frame.frame[position];
            if (currentFrameSide?.reinforcement) {
                reinforcement = core.copy(currentFrameSide.reinforcement);
            }
        } else if (type === 'extension') {
            extension = conf.SideProfiles.find((o) => o.id === extension.id) || {};
            profileId = extension.profileId;
            color = extension.color;
            wood = extension.wood;
            reinforcement = extension.reinforcement ? core.copy(extension.reinforcement) : null;
        } else if (type === 'coupling') {
            coupling = conf.couplings.find((c) => c.id === coupling.id);
            profileId = coupling.profileId;
        }
        const applySideAndUsageFilters =
            type === 'frame' && !changeProfilesWithoutThreshold;
        const profile = this.profilesService.getProfile(profileId);
        if (!color) {
            color = conf.Colors.frame;
        }
        if (type === 'extension' && profile && profile.type === 'sandwich') {
            profile.width = extension.width;
        }
        let frameLength: number | undefined;
        if (type === 'frame' && frame) {
            const frameDrawData = conf.drawData?.frame?.find((f: any) => f.frameId === frame.id)
                ?? conf.drawData?.frame?.[0];
            const currentSide = frame.frame[position]?.side;
            frameLength = frameDrawData?.sides?.find(
                (s: any) => s.outerEdge.side === currentSide
            )?.length;
        }
        [type, options] = this.getOptions(conf, type, {
            position,
            frame,
            sash,
            mullion,
            extension,
            coupling,
            altFrame,
            applySideAndUsageFilters,
            isWholeFrameSelection: changeAllProfiles,
        });
        const singleFrameChange = !changeAllProfiles && !changeProfilesWithoutThreshold && type === 'frame';
        const frameForModal = type === 'frame' ? frame : null;
        if (type !== 'no_mullion') {
            this.openProfilesModal(
                conf, type, options, profile, color, wood, reinforcement, true, undefined, extension, frameLength,
                frameForModal,
                singleFrameChange ? (typeof position === 'number' ? position : null) : null, mullion,
            ).then(
                (selectedProfile) => {
                    if (selectedProfile) {
                        const pauseId = this.eventBusService.pause([
                            'setBondedGlazingInSash',
                            'setMullionProfile',
                            'setSashProfile',
                        ]);
                        try {
                            if (changeAllProfiles && type !== 'mullion') {
                                Object.keys(type === 'frame' ? frame.frame : sash.frame).map(
                                    (pos) => {
                                        position = type === 'frame' ? Number(pos) : pos;
                                        const canBeChanged =
                                            (type !== 'sash' && type !== 'false_mullion_sash') ||
                                            this.canBeChangedSashProfile(conf, position, sash, selectedProfile);
                                        if (canBeChanged) {
                                            this.setProfile(
                                                conf,
                                                defaultConf,
                                                type,
                                                selectedProfile,
                                                {
                                                    position,
                                                    frame,
                                                    sash,
                                                    mullion,
                                                    extension,
                                                }
                                            );
                                        }
                                    }
                                );
                                if (type === 'frame' && frame && selectedProfile.allowManualReinforcementChange) {
                                    const currentFrame = conf.Frames.find((f: any) => f.id === frame.id);
                                    if (currentFrame) {
                                        const selReinforcement = selectedProfile.reinforcement;
                                        Object.keys(frame.frame).forEach((pos) => {
                                            const sideFrame = currentFrame.frame[Number(pos)];
                                            if (sideFrame) {
                                                if (selReinforcement && selReinforcement.automaticSelection === false) {
                                                    sideFrame.reinforcement = core.copy(selReinforcement);
                                                } else {
                                                    sideFrame.reinforcement = null;
                                                }
                                            }
                                        });
                                    }
                                }
                            } else if (changeProfilesWithoutThreshold && type === 'frame') {
                                if (changeAllFrames) {
                                    this.setFramesProfileSet(conf, defaultConf, selectedProfile);
                                } else {
                                    const frameData =
                                        conf.drawData &&
                                        conf.drawData.frame &&
                                        (conf.drawData.frame.find(
                                            (f: any) => f.frameId === frame.id
                                        ) ??
                                            conf.drawData.frame[0]);
                                    Object.keys(frame.frame).map((pos) => {
                                        position = Number(pos);
                                        let frameProfile = this.profilesService.getProfile(
                                            frame.frame[pos].profileId
                                        );
                                        if (frame.frame[pos].alt?.profileId) {
                                            frameProfile = this.profilesService.getProfile(
                                                frame.frame[pos].alt?.profileId
                                            );
                                        }
                                        if (frameProfile.type !== 'threshold') {
                                            this.setProfile(
                                                conf,
                                                defaultConf,
                                                type,
                                                selectedProfile,
                                                {
                                                    position,
                                                    frame,
                                                    sash,
                                                    mullion,
                                                    extension,
                                                    altFrame: frame.frame[pos].alt != null,
                                                    drawPosition:
                                                        position > 0
                                                            ? position +
                                                              (frameData.sides.length -
                                                                  Object.keys(frame.frame).length)
                                                            : position,
                                                }
                                            );
                                        }
                                    });
                                    if (selectedProfile.allowManualReinforcementChange) {
                                        const currentFrame = conf.Frames.find((f: any) => f.id === frame.id);
                                        if (currentFrame) {
                                            const selReinforcement = selectedProfile.reinforcement;
                                            Object.keys(frame.frame).forEach((pos) => {
                                                const posNum = Number(pos);
                                                const sideFrame = currentFrame.frame[posNum];
                                                const fp = this.profilesService.getProfile(
                                                    frame.frame[pos].alt?.profileId ?? frame.frame[pos].profileId
                                                );
                                                if (sideFrame && fp.type !== 'threshold') {
                                                    if (selReinforcement && selReinforcement.automaticSelection === false) {
                                                        sideFrame.reinforcement = core.copy(selReinforcement);
                                                    } else {
                                                        sideFrame.reinforcement = null;
                                                    }
                                                }
                                            });
                                        }
                                    }
                                }
                            } else if (selectedProfile.type === 'coupling' && mullion) {
                                this.framesService.splitFrame(selectedProfile, mullion, conf);
                            } else if (selectedProfile.type === 'fixed_mullion' && coupling) {
                                this.framesService.joinFrame(selectedProfile, coupling, conf);
                            } else {
                                this.setProfile(
                                    conf,
                                    defaultConf,
                                    selectedProfile.type,
                                    selectedProfile,
                                    {
                                        position,
                                        frame,
                                        sash,
                                        mullion,
                                        extension,
                                        coupling,
                                        altFrame,
                                        drawPosition,
                                    }
                                );
                                if ((selectedProfile.type === 'frame' || selectedProfile.type === 'threshold')
                                    && selectedProfile.allowManualReinforcementChange
                                    && frame && typeof position === 'number') {
                                    const currentFrame = conf.Frames.find((f: any) => f.id === frame.id);
                                    if (currentFrame?.frame?.[position]) {
                                        const selReinforcement = selectedProfile.reinforcement;
                                        if (selReinforcement && selReinforcement.automaticSelection === false) {
                                            currentFrame.frame[position].reinforcement = core.copy(selReinforcement);
                                        } else {
                                            currentFrame.frame[position].reinforcement = null;
                                        }
                                    }
                                }
                            }
                        } finally {
                            this.eventBusService.resume(
                                ['setBondedGlazingInSash', 'setMullionProfile', 'setSashProfile'],
                                pauseId
                            );
                        }
                        if (this.config().IccConfig.Configurators.dependencies) {
                            this.eventBusService.post({ key: 'processDependencies', value: null });
                        }
                    }
                }
            );
        }
    }
    getAdjacentFieldsToCoupling(coupling, conf) {
        const frameIds = [
            ...(coupling.framesId?.map(f => f.id) || []),
            ...(coupling.otherFramesId?.map(f => f.id) || [])
        ];

        const adjacentSashes = conf.Sashes.filter(sash =>
            frameIds.includes(sash.frameId)
        );

        return adjacentSashes; 
    }
    private setProfile(
        conf: WindowActiveConfiguration,
        defaultConf: WindowActiveConfiguration,
        type: ProfileType,
        profile: Profile,
        {
            position = null,
            frame = null,
            sash = null,
            mullion = null,
            extension = null,
            coupling = null,
            altFrame = false,
            drawPosition = null,
        }: {
            position?: 'bottom' | 'top' | 'left' | 'right' | number | null;
            frame?: any | null;
            sash?: any | null;
            mullion?: any | null;
            extension?: any | null;
            coupling?: any | null;
            altFrame?: boolean;
            drawPosition?: number | string | null;
        }
    ) {
        if ((type === 'sash' || type === 'false_mullion_sash') && typeof position === 'string') {
            this.profilesService.setSashProfile(conf, defaultConf, sash, profile, position, {});
        } else if (type.indexOf('mullion') > -1) {
            this.profilesService.setMullionProfile(conf, mullion, profile, { clone: true });
        } else if (['frame', 'threshold'].includes(type) && typeof position === 'number') {
            if (!drawPosition) {
                drawPosition = position;
            }
            const frameData = conf.drawData && conf.drawData.frame && conf.drawData.frame[0];
            const side =
                (frameData &&
                    drawPosition &&
                    frameData.sides[drawPosition] &&
                    frameData.sides[drawPosition].outerEdge.side) ||
                'bottom';
            const mainProfile =
                altFrame && frame?.splitBottom
                    ? this.profilesService.getProfile(frame?.frame[position].profileId)
                    : profile;
            const altProfile =
                frame?.frame[position]?.alt &&
                (altFrame && frame?.splitBottom
                    ? profile
                    : this.profilesService.getProfile(frame?.frame[position]?.alt?.profileId));
            this.profilesService.setFrameProfile(conf, mainProfile, frame, position, {
                side,
                finWidth:
                    altProfile && side === 'bottom'
                        ? altProfile.finWidth || 0
                        : profile && (side !== 'top' || (side === 'top' && !conf.hasRoller))
                        ? profile.finWidth || 0
                        : 0,
                altProfile,
                clone: !!(mainProfile && mainProfile.widthOutType),
            });
        } else if (type === 'extension' || type === 'sandwich') {
            this.profilesService.setExtensionProfile(extension, profile);
        } else if (type === 'coupling') {
            this.framesService.changeCouplingProfile(conf, coupling, profile, {});
        }
    }

    setFramesProfileSet(
        conf: WindowActiveConfiguration,
        defaultConf: WindowActiveConfiguration,
        profile: Profile
    ) {
        const profileSet = core.copy(conf.ProfileSet);
        profileSet.frameTop = profile.id;
        profileSet.frameSide = profile.id;
        profileSet.frameBottom = profile.id;
        conf.ProfileSet = core.copy(profileSet);
        conf.ProfileSet.isDefault = false;
        defaultConf.ProfileSet = core.copy(profileSet);
        defaultConf.ProfileSet.isDefault = false;
        this.eventBusService.post({
            key: 'setProfileSet',
            value: {
                profileSet,
            },
            conf,
            defaultConf,
        });
    }

    private getMullion(conf: WindowActiveConfiguration, mullionId: number) {
        for (const mullion of conf.Mullions) {
            if (mullion.id === mullionId) {
                return mullion;
            }
        }
        for (const sash of conf.Sashes) {
            for (const mullion of sash.intMullions) {
                if (mullion.id === mullionId) {
                    return mullion;
                }
            }
        }
    }

    private getSash(conf: WindowActiveConfiguration, sashId: number) {
        for (const sash of conf.Sashes) {
            if (sash.id === sashId) {
                return sash;
            }
        }
    }

    getOptions(
        conf: WindowActiveConfiguration,
        type: any,
        {
            position = null,
            frame = null,
            sash = null,
            mullion = null,
            extension = null,
            coupling = null,
            altFrame = false,
            applySideAndUsageFilters = false,
            isWholeFrameSelection = false,
        }: {
            position?: 'bottom' | 'top' | 'left' | 'right' | 1 | null;
            frame?: any;
            sash?: any;
            mullion?: any;
            extension?: any;
            coupling?: any;
            altFrame?: boolean;
            applySideAndUsageFilters?: boolean;
            isWholeFrameSelection?: boolean;
        }
    ) {
        let options:
            | {
                  and: string[];
                  not: string[];
                  filter?: (
                      profile: Profile,
                      conf: WindowActiveConfiguration | WindowConfiguration | null
                  ) => boolean;
              }
            | {
                  and: string[];
                  not: string[];
                  filter?: (
                      profile: Profile,
                      conf: WindowActiveConfiguration | WindowConfiguration | null
                  ) => boolean;
              }[] = {
            and: [],
            not: [],
        };

        let positionMap: {
            bottom: string;
            right: string;
            top: string;
            left: string;
            0?: string;
            1?: string;
            2?: string;
            3?: string;
        } = {
            0: 'bottom',
            1: 'side',
            2: 'top',
            3: 'side',
            bottom: 'bottom',
            right: 'side',
            top: 'top',
            left: 'side',
        };

        if (position !== null) {
            if (type === 'sashFrame' && sash) {
                const sashFrameData =
                    conf.drawData &&
                    conf.drawData.sashFrame &&
                    conf.drawData.sashFrame.find((s) => s.sashId === sash.id);

                if (sashFrameData) {
                    sashFrameData.sides.forEach((side, index) => {
                        positionMap[index] = side.outerEdge.side || 'bottom';
                    });
                }
                if (sash.nearMullions.bottom > -1) {
                    const bottomMulion = conf.Mullions.find(
                        (el) => el.id === sash.nearMullions.bottom
                    );
                    const bottomSashes = (bottomMulion && bottomMulion.multiAlignBottom) || [];

                    if (
                        sash.type.type === 'SD' ||
                        bottomSashes.some((el) => el.type && el.type.type === 'SU')
                    ) {
                        positionMap = {
                            top: 'top_in_top_sash',
                            bottom: 'bottom_in_top_sash',
                            left: 'side_in_top_sash',
                            right: 'side_in_top_sash',
                        };
                    }
                } else if (sash.nearMullions.top > -1) {
                    const topMullion = conf.Mullions.find((el) => el.id === sash.nearMullions.top);
                    const topSashes = (topMullion && topMullion.multiAlignTop) || [];

                    if (
                        sash.type.type === 'SU' ||
                        topSashes.some((el) => el.type && el.type.type === 'SD')
                    ) {
                        positionMap = {
                            top: 'top_in_bottom_sash',
                            bottom: 'bottom_in_bottom_sash',
                            left: 'side_in_bottom_sash',
                            right: 'side_in_bottom_sash',
                        };
                    }
                }
            }
            options.and.push(positionMap[position]);

            if (type === 'sashFrame' && sash) {
                if (
                    (position === 'right' || position === 'left') &&
                    sash.nearMullions[position] > -1 &&
                    sash.type.handle_position === (position === 'right' ? 'R' : 'L')
                ) {
                    const nearbyMullion = conf.Mullions.find(
                        (el) => el.id === sash.nearMullions[position]
                    );
                    const adjacentSashes = nearbyMullion?.[position === 'right' ? 'multiAlignRight' : 'multiAlignLeft'] || [];
                    if (
                        !sash.type.passive &&
                        nearbyMullion?.type === 'false_mullion' &&
                        adjacentSashes.length > 0
                    ) {
                        const passiveSashProfile = this.profilesService.getProfile(
                            adjacentSashes[0]?.frame?.[position === 'right' ? 'left' : 'right']?.profileId
                        );
                        if (passiveSashProfile?.compatibleActiveSashProfileId) {
                            options.filter = (profile, conf) => (
                                +passiveSashProfile?.compatibleActiveSashProfileId ===
                                    profile.id
                            );
                        }
                    }

                    if (!sash.type.passive) {
                        options.not.push('passive_sash');
                    }
                } else {
                    options.not.push('passive_sash');
                }
            }
        }

        if (type === 'sashFrame' && sash) {
            type = 'sash';
            options[sash.type.out_open ? 'and' : 'not'].push('outward_opening');
            if (
                sash.type.passive &&
                ((sash.type.handle_position === 'R' && position === 'right') ||
                    (sash.type.handle_position === 'L' && position === 'left'))
            ) {
                type = ['sash', 'false_mullion_sash'];
                options = [
                    options,
                    {
                        and: [],
                        not: [],
                    },
                ];
                options[1][sash.type.out_open ? 'and' : 'not'].push('outward_opening');
            }
        } else if (type === 'mullion' && mullion) {
            type = mullion.type;
            if (type === 'fixed_mullion') {
                options.and.push(
                    mullion.direction + (mullion.parentSash != null ? '_sash' : '_frame'),
                    this.mullionsService.getMullionGlazingOrHardwareOptions(mullion)
                );
                const frame =
                    mullion.frameId != null
                        ? FramesService.getFrameById(mullion.frameId, conf)
                        : null;

                if (
                    !mullion.parentSash &&
                    ((mullion.direction === 'vertical' &&
                        (mullion.rHeight === conf.Height ||
                            (frame && mullion.rHeight === frame.height))) ||
                        (mullion.direction === 'horizontal' &&
                            (mullion.rWidth === conf.Width ||
                                (frame && mullion.rWidth === frame.width)))) &&
                    this.config().IccConfig.Configurators.coupledFrames
                ) {
                    type = [type, 'coupling'];
                    options = [
                        options,
                        {
                            and: [],
                            not: [],
                        },
                    ];
                }
            } else {
                const centralHandleOption = this.mullionsService.hasCentralHandleFalseMullion(mullion);
                if (centralHandleOption) {
                    options.and.push('central_handle');
                } else {
                    options.not.push('central_handle');
                }
            }
        } else if (type === 'extension') {
            type = ['extension', 'sandwich'];
            options = [
                options,
                {
                    and: [],
                    not: [],
                },
            ];
        } else if (
            type === 'coupling' &&
            coupling
        ) {
            if (coupling.framesId.length === 1 && coupling.otherFramesId.length === 1) {
                type = ['fixed_mullion', 'coupling'];
                const adjacentFields = this.getAdjacentFieldsToCoupling(coupling, conf);
                let glazingKey = '';

                if (adjacentFields.length === 2) {
                    const fCount = adjacentFields.filter(f => f.type?.type === 'F').length;

                    if (fCount === 2) {
                        glazingKey = 'for_glazing';
                    } else if (fCount === 0) {
                        glazingKey = 'for_hardware';
                    } else {
                        glazingKey = 'for_glazing_or_hardware';
                    }
                }
                options = [
                {
                    and: [
                        coupling.direction + (mullion?.parentSash ? '_sash' : '_frame'),
                        ...(glazingKey ? [glazingKey] : [])
                    ],
                    not: [],
                },
                {
                    and: [],
                    not: [],
                },
            ];
            }
        } else if (type === 'frame') {
            if (frame.lowThreshold && !altFrame && positionMap[position] === 'bottom') {
                type = 'threshold';
            }

            const frameSidesToValidate =
                isWholeFrameSelection
                    ? (frame?.frame || [])
                          .map((side: any) => side?.side)
                          .filter((side: any) => side === 'left' || side === 'right' || side === 'top' || side === 'bottom')
                    : [];
            const selectedFrameSide =
                typeof position === 'number' ? frame?.frame?.[position]?.side : null;
            const hasSashOnSelectedSide =
                applySideAndUsageFilters
                && selectedFrameSide
                && this.hasActiveSashAdjacentToFrameSide(conf, frame, selectedFrameSide);
            const hasSashOnAnySelectedSide =
                applySideAndUsageFilters
                && isWholeFrameSelection
                && frameSidesToValidate.some((side) =>
                    this.hasActiveSashAdjacentToFrameSide(conf, frame, side)
                );
            const hasGlazingOnAnySelectedSide =
                applySideAndUsageFilters
                && isWholeFrameSelection
                && frameSidesToValidate.some((side) =>
                    !this.hasActiveSashAdjacentToFrameSide(conf, frame, side)
                );
            const requiredUsageOption = hasSashOnSelectedSide ? 'for_hardware' : 'for_glazing';
            const requiredSideOption = selectedFrameSide === 'bottom' ? 'bottom' : 'side';

            options = {
                and: [],
                not: [],
                filter: (profile, conf) => {
                    const doorLightSizeMatched =
                        (conf && this.profilesService.matchDoorLightSize(profile, conf)) || false;

                    if (!doorLightSizeMatched || type !== 'frame' || !applySideAndUsageFilters) {
                        return doorLightSizeMatched;
                    }

                    const profileOptions = profile.options || [];

                    if (isWholeFrameSelection) {
                        const hasMixedUsageNeeds =
                            hasSashOnAnySelectedSide
                            && hasGlazingOnAnySelectedSide;

                        if (profileOptions.includes('for_glazing_or_hardware')) {
                            return true;
                        }

                        if (hasMixedUsageNeeds) {
                            return (
                                profileOptions.includes('for_hardware')
                                && profileOptions.includes('for_glazing')
                            );
                        }

                        if (hasSashOnAnySelectedSide) {
                            return profileOptions.includes('for_hardware');
                        }

                        return profileOptions.includes('for_glazing');
                    }

                    return (
                        profileOptions.includes('for_glazing_or_hardware')
                        || profileOptions.includes(requiredUsageOption)
                    );
                },
            };

            if (type === 'frame') {
                const hasOutOpenSash = conf.Sashes.some(
                    (s) => s.frameId === frame.id && s.type?.out_open
                );
                if (applySideAndUsageFilters) {
                    if (isWholeFrameSelection) {
                        const hasBottomSide = frameSidesToValidate.includes('bottom');
                        const hasNonBottomSide = frameSidesToValidate.some(
                            (side) => side !== 'bottom'
                        );

                        if (hasBottomSide) {
                            options.and.push('bottom');
                        }

                        if (hasNonBottomSide) {
                            options.and.push('side');
                        }
                    } else {
                        options.and.push(requiredSideOption);
                    }
                }
                if (hasOutOpenSash) {
                    options.and.push('for_outward_opening_window');
                } else {
                    options.and.push('for_inward_opening_window');
                }
            }
        }

        return [type, options];
    }

    private hasActiveSashAdjacentToFrameSide(
        conf: WindowActiveConfiguration,
        frame: any,
        side: 'left' | 'right' | 'top' | 'bottom' | 'side'
    ): boolean {
        if (!frame) {
            return false;
        }

        return conf.Sashes.some((sash) => {
            if (sash.frameId !== frame.id || !Array.isArray(sash.intSashes) || sash.intSashes.length === 0) {
                return false;
            }

            const isOnLeftEdge =
                ((conf.EdgeSashes?.left || []).includes(sash.id))
                || (sash.nearMullions?.left === -1 && sash.nearAlignments?.left === -1);
            const isOnRightEdge =
                ((conf.EdgeSashes?.right || []).includes(sash.id))
                || (sash.nearMullions?.right === -1 && sash.nearAlignments?.right === -1);
            const isOnTopEdge =
                ((conf.EdgeSashes?.top || []).includes(sash.id))
                || (sash.nearMullions?.top === -1 && sash.nearAlignments?.top === -1);
            const isOnBottomEdge =
                ((conf.EdgeSashes?.bottom || []).includes(sash.id))
                || (sash.nearMullions?.bottom === -1 && sash.nearAlignments?.bottom === -1);

            switch (side) {
                case 'left':
                    return isOnLeftEdge;
                case 'right':
                    return isOnRightEdge;
                case 'top':
                    return isOnTopEdge;
                case 'bottom':
                    return isOnBottomEdge;
                case 'side':
                    return isOnLeftEdge || isOnRightEdge;
                default:
                    return false;
            }
        });
    }

    canBeChangedSashProfile(
        conf: WindowActiveConfiguration,
        position: 'left' | 'right' | 'top' | 'bottom',
        sash: ActiveSash,
        selectedProfile: Profile,
    ) {
        const nearMullion = sash.nearMullions[position];
        if (nearMullion > -1) {
            const mullion = this.getMullion(conf, nearMullion);
            if (mullion?.type === 'false_mullion') {
                if (
                    sash.type?.passive &&
                    sash.type?.handle_position === (position === 'right' ? 'R' : 'L') &&
                    !this.profilesService.isProfileCompatibleWithProfile(mullion.profileId, selectedProfile.id)
                ) {
                    return false;
                }
                const nearSash =
                    position === 'right' ? mullion.multiAlignRight[0] : mullion.multiAlignLeft[0];
                if (
                    nearSash &&
                    nearSash.type?.passive &&
                    nearSash.type?.handle_position === (position === 'right' ? 'L' : 'R')
                ) {
                    const nearSashProfile = this.profilesService.getProfile(
                        nearSash.frame[position === 'right' ? 'left' : 'right'].profileId
                    );
                    if (nearSashProfile.compatibleActiveSashProfileId) {
                        return nearSashProfile.compatibleActiveSashProfileId === selectedProfile.id;
                    }
                }
            }
        }
        return true;
    }

    hasAvailableFrameMullionProfiles(
        conf: WindowActiveConfiguration,
        direction: 'horizontal' | 'vertical'
    ): boolean {
        const profiles = this.profilesService.getProfilesByType('fixed_mullion');
        const option = direction === 'horizontal' ? 'horizontal_frame' : 'vertical_frame';
        const filteredProfiles = profiles.filter(
            profile => profile.systems.includes(conf.System.id) && profile.options.includes(option)
        );
        return filteredProfiles.length > 0;
    }

    hasAvailableSashMullionProfiles(
        conf: WindowActiveConfiguration,
        direction: 'horizontal' | 'vertical'
    ): boolean {
        const profiles = this.profilesService.getProfilesByType('fixed_mullion');
        const option = direction === 'horizontal' ? 'horizontal_sash' : 'vertical_sash';
        const filteredProfiles = profiles.filter(
            profile => profile.systems.includes(conf.System.id) && profile.options.includes(option)
        );
        return filteredProfiles.length > 0;
    }
}
